/**
 * How a waitlist request's availability reads to a person.
 *
 * Here rather than in the browser because the server now writes it into
 * messages too — a tutor's "new waitlist request" and a family's "you're on
 * the waitlist" both have to say which times were asked for, and two
 * implementations of that would eventually disagree about a Tuesday.
 */

export const WAITLIST_WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export interface WaitlistPatternShape {
  weekday: number;
  startsMinutes: number;
  endsMinutes: number;
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function describeWaitlistPattern(pattern: WaitlistPatternShape): string {
  return `${WAITLIST_WEEKDAYS[pattern.weekday] ?? "Unknown day"} ${clock(pattern.startsMinutes)}–${clock(pattern.endsMinutes)}`;
}

/** Every requested window, as one line. Empty when a request somehow has none. */
export function describeWaitlistPatterns(
  patterns: readonly WaitlistPatternShape[],
): string {
  return patterns.map(describeWaitlistPattern).join(", ");
}

/** Phase 4I notice policy: inside this, the tutor must approve. FastAPI still owns move/cancel notice. */
export const TIME_REQUEST_NOTICE_MS = 24 * 60 * 60 * 1000;
export const MIN_REQUEST_RUNWAY_MS = 2 * 60 * 60 * 1000;
export const FALLBACK_LEAD_MS = 60 * 60 * 1000;
export const MAX_OPEN_TIME_REQUESTS = 5;
export const FULL_SWEEP_INTERVAL_MS = 60 * 60 * 1000;

export function timeRequestClosesAt(request: { purpose: string | null; actBy: Date | null }): Date | null {
  return request.actBy === null ? null : new Date(request.actBy.getTime() - (request.purpose === "move" ? FALLBACK_LEAD_MS : 0));
}

/** Offers must end before the requested lesson starts; a move's fallback keeps its own schedule. */
export function timeRequestOfferClosesAt(request: {
  purpose: string | null;
  actBy: Date | null;
  requestedStartsAt: Date | null;
}): Date | null {
  const closesAt = timeRequestClosesAt(request);
  return closesAt === null || request.requestedStartsAt === null
    ? null
    : new Date(Math.min(closesAt.getTime(), request.requestedStartsAt.getTime()));
}
